import { supabase } from "../supabaseClient";
import { useEffect, useState } from "react";
import { useSearch } from './AdminSearchContext';

const AdminCarts = () => {
  const { searchTerm } = useSearch();
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); 
  
  const filteredCarts = carts.filter(cart => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    
    const cartFields = [
      cart.id?.toString(),
      cart.full_name,
      cart.email,
      cart.numero_telephone,
      cart.address,
      cart.status,
      cart.created_at ? new Date(cart.created_at).toLocaleString() : ''
    ].some(field => field?.toLowerCase().includes(term));
    
    const productFields = Array.isArray(cart.products) && 
      cart.products.some(product => {
        const productSearchFields = [
          product?.id?.toString(),
          product?.name,
          product?.description
        ];
        return productSearchFields.some(field => field?.toLowerCase().includes(term));
      });
    
    return cartFields || productFields;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCarts = filteredCarts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCarts.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchCarts();
    } else {
      setLoading(false);
      setCarts([]);
    }
  }, [user]);

  const fetchCarts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("carts")
      .select(`
        id,
        created_at,
        quantity,
        full_name,
        email,
        numero_telephone,
        address,
        status,
        products (
          name,
          price,
          image_url
        )
      `)
      .order("created_at", { ascending: false });

    if (error) console.error("Erreur lors du chargement des commandes :", error);
    else setCarts(data);

    setLoading(false);
  };

  const markAsOrdered = async (id) => {
    const { error } = await supabase
      .from("carts")
      .update({ status: "ordered" })
      .eq("id", id);

    if (error) alert("Erreur lors de la mise à jour");
    else fetchCarts();
  };

  const deleteCart = async (id) => {
    const { error } = await supabase
      .from("carts")
      .delete()
      .eq("id", id);

    if (error) alert("Erreur lors de la suppression");
    else fetchCarts();
  };

  if (loading) return <p>Chargement...</p>;
  if (!user) return <p>Connectez-vous pour voir les commandes.</p>;

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <h3 className="page-title">Commandes reçues</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item" style={{color: "#0063cc"}}>Tables</li>
            <li className="breadcrumb-item active" aria-current="page">Commandes</li>
          </ol>
        </nav>
      </div>

      <div className="row">
      <div className="col-lg-12 grid-margin stretch-card">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">Liste des commandes</h4>
            {searchTerm && (
              <p className="text-muted">
                Résultats pour : "{searchTerm}" ({filteredCarts.length} commandes trouvées)
              </p>
            )}
            
            <div className="table-responsive custom-scroll">
              {filteredCarts.length === 0 ? (
                <p>
                  {searchTerm 
                    ? `Aucune commande trouvée pour "${searchTerm}"`
                    : 'Aucune commande reçue.'}
                </p>
              ) : (
                <>
                  <table className="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Image</th>
                        <th>Produit</th>
                        <th>Quantité</th>
                        <th>Nom complet</th>
                        <th>Email</th>
                        <th>Téléphone</th>
                        <th>Adresse</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCarts.map((cart, index) => (
                        <tr key={cart.id}>
                          <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                          <td>
                            {cart.products?.image_url ? (
                              <img
                                src={cart.products.image_url}
                                alt={cart.products.name}
                                style={{ width: "30px", height: "30px", objectFit: "cover" }}
                              />
                            ) : (
                              <span>Pas d'image</span>
                            )}
                          </td>
                          <td>{cart.products?.name || "Produit inconnu"}</td>
                          <td>{cart.quantity}</td>
                          <td>{cart.full_name}</td>
                          <td>{cart.email || "-"}</td>
                          <td>{cart.numero_telephone || "-"}</td>
                          <td>{cart.address}</td>
                          <td>{cart.status || "en attente"}</td>
                          <td>{new Date(cart.created_at).toLocaleString()}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-success mr-2"
                              onClick={() => markAsOrdered(cart.id)}
                              disabled={cart.status === "ordered"}
                            >
                              Commander
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteCart(cart.id)}
                            >
                              Annuler
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                 
                </>
              )}
            </div>
                  {filteredCarts.length > 0 && (
                    <nav aria-label="Page navigation" className="mt-4">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={goToPreviousPage} aria-label="Previous">
                            <span aria-hidden="true">&laquo;</span>
                          </button>
                        </li>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => paginate(page)}>
                              {page}
                            </button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={goToNextPage} aria-label="Next">
                            <span aria-hidden="true">&raquo;</span>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default AdminCarts;